"use client"

import { useEffect, useState } from 'react'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectMainState } from '@/msgdeskRuntime/state/selectors'
import {
  acceptPoa,
  acceptPoaWithReveal,
  downloadOppEvidencePackage,
  downloadOppPublicCertificate,
  generateOppMasterCode,
  loadUnlockedPooContent,
  openPoo,
  openPooWithReveal,
  prepareOppRegistrationEmail,
  prepareOppRegistrationEmailFromCommitments,
  prepareOppSenderAttestationEmail,
  registerOppInternalCommitments,
  sendOppSenderAttestationEmail,
  sendOppRegistrationEmail,
  sendOppRegistrationEmailFromCommitments,
  submitOppDispute,
} from '../protocol.client'
import { PooTransparencyTimeline } from './PooTransparencyTimeline'

function Status({
  label,
  value,
  detail,
}: {
  label: string
  value: string
  detail: string
}) {
  return (
    <div className="rounded-lg border border-[#ddd7cb] bg-[#faf9f6] p-3">
      <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#758079]">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-[#26332f]">{value.replaceAll('_', ' ')}</dd>
      <p className="mt-1 text-xs leading-5 text-[#6b756f]">{detail}</p>
    </div>
  )
}

export function PooProtocolPanel() {
  const { state } = useSubsStore()
  const protocol = state.pooProtocol
  const main = selectMainState(state)
  const [masterCode, setMasterCode] = useState('')
  const [showCode, setShowCode] = useState(false)
  const [codeSaved, setCodeSaved] = useState(false)
  const [strongOffline, setStrongOffline] = useState(() => protocol?.evidenceProfileId === 'strong-external')
  const [offlineOpeningCommitment, setOfflineOpeningCommitment] = useState('')
  const [offlineAcceptanceCommitment, setOfflineAcceptanceCommitment] = useState('')
  const [offlineReveal, setOfflineReveal] = useState('')
  const [pending, setPending] = useState<'sender-email-app' | 'sender-email-send' | 'internal' | 'email-app' | 'email-send' | 'open' | 'accept' | 'load' | 'dispute' | ''>('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [disputeReason, setDisputeReason] = useState('')

  useEffect(() => {
    if (!protocol) return
    if (protocol.allowedActions.registerInternalCommitments
      || (protocol.protocolVersion === 22 && protocol.allowedActions.prepareEmailReply)) {
      setMasterCode(current => current || generateOppMasterCode())
      setCodeSaved(false)
      return
    }
    if (protocol.allowedActions.prepareEmailReply) {
      setMasterCode('')
      setCodeSaved(false)
      return
    }
    if (protocol.openingCommitment) {
      setMasterCode('')
      setShowCode(false)
      setCodeSaved(false)
    }
  }, [protocol])

  useEffect(() => {
    if (protocol?.evidenceProfileId === 'strong-external') setStrongOffline(true)
  }, [protocol?.messageUuid, protocol?.evidenceProfileId])

  useEffect(() => {
    if (!protocol || protocol.openingState !== 'released' || protocol.fullContent) return
    setPending('load')
    loadUnlockedPooContent(protocol)
      .catch(reason => setError(reason instanceof Error ? reason.message : String(reason)))
      .finally(() => setPending(''))
  }, [protocol])

  if (!protocol) {
    return <div className="mb-6 rounded-xl border border-[#b64d46] bg-[#fff5f4] p-5 text-sm text-[#77342f]">The OPP record is unavailable.</div>
  }

  const run = async (action: 'internal' | 'email-app' | 'email-send' | 'open' | 'accept' | 'dispute') => {
    setPending(action)
    setError('')
    setNotice('')
    try {
      if (action === 'internal') {
        await registerOppInternalCommitments(protocol, masterCode)
        setMasterCode(masterCode.trim())
        setNotice('Your commitments were registered by the authenticated MsgDesk recipient session and added to the signed evidence timeline.')
      } else if (action === 'email-app') {
        const registration = strongOffline
          ? prepareOppRegistrationEmailFromCommitments(protocol, offlineOpeningCommitment, offlineAcceptanceCommitment, main.codeKey ?? '')
          : await prepareOppRegistrationEmail(protocol, masterCode, main.codeKey ?? '')
        if (!strongOffline) setMasterCode(masterCode.trim())
        window.location.href = registration.href
        setNotice('Your mail app was opened. Send the prepared reply from the exact recipient mailbox, then return here.')
      } else if (action === 'email-send') {
        if (strongOffline) {
          await sendOppRegistrationEmailFromCommitments(protocol, offlineOpeningCommitment, offlineAcceptanceCommitment)
        } else {
          await sendOppRegistrationEmail(protocol, masterCode)
          setMasterCode(masterCode.trim())
        }
        setNotice('MsgDesk queued a reply-ready message to the recipient mailbox. Reply to it without changing the subject or commitment block.')
      } else if (action === 'open') {
        if (strongOffline) {
          await openPooWithReveal(protocol, offlineReveal)
          setOfflineReveal('')
          setNotice('The offline opening reveal matched the frozen commitment. Opening was recorded; Part 2 releases after anchor acknowledgement.')
        } else {
          const result = await openPoo(protocol, masterCode)
          setMasterCode('')
          setNotice(`${result.whitespaceRemoved ? 'Surrounding whitespace from the pasted code was removed. ' : ''}Opening was recorded. Part 2 releases after the external anchor acknowledges that event.`)
        }
      } else if (action === 'accept') {
        if (strongOffline) {
          await acceptPoaWithReveal(protocol, offlineReveal)
          setOfflineReveal('')
          setNotice('The offline acceptance reveal matched the separately frozen commitment. Explicit acceptance was recorded.')
        } else {
          const result = await acceptPoa(protocol, masterCode)
          setMasterCode('')
          setNotice(`${result.whitespaceRemoved ? 'Surrounding whitespace from the pasted code was removed. ' : ''}Explicit acceptance was recorded.`)
        }
      } else {
        await submitOppDispute(protocol, disputeReason)
        setDisputeReason('')
        setNotice('Your dispute was recorded in the append-only timeline.')
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setPending('')
    }
  }

  const registrationMode = protocol.allowedActions.registerInternalCommitments || protocol.allowedActions.prepareEmailReply
  const submitCodeAction = protocol.allowedActions.open
    ? 'open'
    : protocol.allowedActions.accept
      ? 'accept'
      : null

  return (
    <section data-md-contract="poo.protocol" className="mb-6 rounded-xl border border-[#c9c3b7] bg-white p-5 shadow-[0_1px_4px_rgba(28,40,36,0.07)] sm:p-7">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#68746f]">OPP v{protocol.protocolVersion} · {protocol.evidenceProfileId.replace('-', ' ')}</div>
          <h2 className="mt-1 text-xl font-semibold text-[#1d2a26]">Proof controls and evidence</h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-[#68716d]">
            Opening, explicit acceptance, provider delivery, disputes, and external anchors are separate facts.
          </p>
        </div>
        <span className="rounded-full border border-[#bdc9c4] bg-[#f3f7f5] px-3 py-1.5 text-xs font-semibold text-[#315f52]">
          {protocol.lifecycleState.replaceAll('_', ' ')}
        </span>
      </header>

      {error ? <p role="alert" className="mt-4 rounded-md border border-[#b64d46] bg-[#fff5f4] px-4 py-3 text-sm text-[#77342f]">{error}</p> : null}
      {notice ? <p role="status" className="mt-4 rounded-md border border-[#9fc4b4] bg-[#f1f8f5] px-4 py-3 text-sm text-[#315f52]">{notice}</p> : null}

      <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Status label="Commitment attribution" value={protocol.evidenceProfile.recipientEnrollment} detail={protocol.evidenceProfileId === 'basic-internal' ? 'The authenticated internal recipient registers commitments on site.' : 'Only a valid independently authenticated reply can seal the commitments.'} />
        <Status label="Opening" value={protocol.openingState} detail="Opening and later access are different: the reveal is never reused as a password." />
        <Status label="Recipient copy" value={protocol.recipientCopyState} detail={protocol.recipientCopyState === 'not-applicable' ? 'This Basic OPP has no external recipient-copy destination.' : 'Submitted or queued is not delivery. Only a provider event starts the dispute window.'} />
        <Status label="External anchor" value={protocol.anchorState} detail="Acknowledged and finalized are shown as distinct states." />
      </dl>

      {protocol.allowedActions.prepareSenderAttestation ? (
        <div className="mt-6 rounded-xl border border-[#b6a1c7] bg-[#faf7fc] p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-[#463452]">Strong sender offer attestation</h3>
          <p className="mt-1 text-xs leading-5 text-[#675870]">Confirm the exact offer hash and both internal parties from your independently operated external mailbox. This attests the offer; it does not open or accept it.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={Boolean(pending)}
              onClick={() => {
                try {
                  window.location.href = prepareOppSenderAttestationEmail(protocol, main.codeKey ?? '').href
                  setNotice('Your mail app was opened. Send the prepared attestation from the exact frozen sender mailbox.')
                } catch (reason) {
                  setError(reason instanceof Error ? reason.message : String(reason))
                }
              }}
              className="h-11 rounded-md bg-[#5d4770] px-5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {pending === 'sender-email-app' ? 'Preparing…' : 'Open sender attestation in email app'}
            </button>
            <button
              type="button"
              disabled={Boolean(pending)}
              onClick={() => {
                setPending('sender-email-send')
                setError('')
                setNotice('')
                void sendOppSenderAttestationEmail(protocol)
                  .then(() => setNotice('MsgDesk queued a reply-ready message to the frozen sender mailbox. Reply without editing the attestation block.'))
                  .catch(reason => setError(reason instanceof Error ? reason.message : String(reason)))
                  .finally(() => setPending(''))
              }}
              className="h-11 rounded-md border border-[#5d4770] bg-white px-5 text-sm font-semibold text-[#5d4770] disabled:opacity-50"
            >
              {pending === 'sender-email-send' ? 'Sending…' : 'Email sender a reply-ready attestation'}
            </button>
          </div>
        </div>
      ) : null}

      {(protocol.allowedActions.registerInternalCommitments || protocol.allowedActions.prepareEmailReply || protocol.allowedActions.open || protocol.allowedActions.accept) ? (
        <form
          className="mt-6 rounded-xl border border-[#d7c89f] bg-[#fffaf0] p-4 sm:p-5"
          onSubmit={event => {
            event.preventDefault()
            if (submitCodeAction) void run(submitCodeAction)
          }}
        >
          <label className="block text-sm font-semibold text-[#3c443f]" htmlFor="opp-master-code">
            {strongOffline
              ? registrationMode ? 'Paste commitments from the offline OPP tool' : 'Paste the purpose-specific reveal from the offline OPP tool'
              : registrationMode ? 'Create and save your OPP code' : 'Enter the OPP code you registered'}
          </label>
          <p className="mt-1 text-xs leading-5 text-[#6f674f]">
            {strongOffline
              ? 'The high-value path keeps the master code outside MsgDesk. Download the standalone tool, save and verify it, disconnect it from the network, and paste only its commitments or one-time reveal here.'
              : registrationMode
              ? protocol.evidenceProfileId === 'strong-external'
                ? 'Create this high-entropy code outside MsgDesk, store it in your password manager, then enter it to prepare the external-mail attestation. MsgDesk does not receive the code itself.'
                : 'Store this code in your password manager before registering its commitments. MsgDesk does not receive the code itself.'
              : 'Use the exact code you stored during commitment registration. It is checked locally before its purpose-specific reveal is sent.'}
          </p>
          {protocol.evidenceProfileId === 'strong-external' ? (
            <label className="mt-3 flex items-start gap-2 text-xs font-semibold text-[#51462f]">
              <input type="checkbox" checked={strongOffline} onChange={event => {
                setStrongOffline(event.target.checked)
                setCodeSaved(false)
              }} className="mt-0.5" />
              <span>Maximum path: keep my master code outside MsgDesk and use the standalone offline tool.</span>
            </label>
          ) : null}
          {strongOffline && protocol.evidenceProfileId === 'strong-external' ? (
            <div className="mt-3 space-y-3 rounded-lg border border-[#d7c89f] bg-white p-3">
              <a href="/opp-v23-offline.html" download className="inline-flex rounded-md border border-[#9a8660] px-3 py-2 text-xs font-semibold text-[#675630]">Download standalone OPP v23 offline tool</a>
              <dl className="grid gap-2 text-xs text-[#6f674f]">
                <div><dt className="font-semibold">Document UUID</dt><dd className="break-all font-mono">{protocol.messageUuid}</dd></div>
                <div><dt className="font-semibold">Offer SHA-256</dt><dd className="break-all font-mono">{protocol.offerHash}</dd></div>
                <div><dt className="font-semibold">Recipient party</dt><dd className="break-all font-mono">{protocol.recipientId}</dd></div>
              </dl>
              {registrationMode ? (
                <div className="grid gap-3">
                  <label className="text-xs font-semibold">C_open<input value={offlineOpeningCommitment} onChange={event => { setOfflineOpeningCommitment(event.target.value); setCodeSaved(false) }} className="mt-1 h-10 w-full rounded-md border border-[#b9aa87] px-3 font-mono font-normal" /></label>
                  <label className="text-xs font-semibold">C_accept<input value={offlineAcceptanceCommitment} onChange={event => { setOfflineAcceptanceCommitment(event.target.value); setCodeSaved(false) }} className="mt-1 h-10 w-full rounded-md border border-[#b9aa87] px-3 font-mono font-normal" /></label>
                </div>
              ) : (
                <label className="block text-xs font-semibold">{protocol.allowedActions.accept ? 'R_accept' : 'R_open'}<input value={offlineReveal} onChange={event => setOfflineReveal(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-[#b9aa87] px-3 font-mono font-normal" /></label>
              )}
            </div>
          ) : (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              id="opp-master-code"
              name="opp-master-code"
              type={showCode ? 'text' : 'password'}
              autoComplete={registrationMode ? 'new-password' : 'current-password'}
              required
              value={masterCode}
              onChange={event => {
                setMasterCode(event.target.value)
                if (registrationMode) setCodeSaved(false)
              }}
              className="h-11 min-w-0 flex-1 rounded-md border border-[#b9aa87] bg-white px-3 font-mono text-sm text-[#26332f]"
            />
            <button type="button" onClick={() => setShowCode(value => !value)} className="h-11 rounded-md border border-[#9a8660] bg-white px-4 text-sm font-semibold text-[#675630]">
              {showCode ? 'Hide' : 'Show'}
            </button>
            {registrationMode && protocol.evidenceProfileId !== 'strong-external' ? (
              <button type="button" onClick={() => {
                setMasterCode(generateOppMasterCode())
                setCodeSaved(false)
              }} className="h-11 rounded-md border border-[#9a8660] bg-white px-4 text-sm font-semibold text-[#675630]">
                Generate
              </button>
            ) : null}
          </div>
          )}
          {registrationMode ? (
            <label className="mt-4 flex items-start gap-2 text-sm font-medium text-[#51462f]">
              <input
                type="checkbox"
                checked={codeSaved}
                disabled={Boolean(pending)}
                onChange={event => setCodeSaved(event.target.checked)}
                className="mt-0.5"
              />
              <span>{strongOffline ? 'I saved the offline tool output and master code outside MsgDesk and verified the document, offer, and recipient inputs.' : 'I saved this exact code outside MsgDesk and understand that it cannot be recovered.'}</span>
            </label>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-3">
            {protocol.allowedActions.registerInternalCommitments ? (
              <button type="button" disabled={Boolean(pending) || !codeSaved} onClick={() => void run('internal')} className="h-11 rounded-md bg-[#315f52] px-5 text-sm font-semibold text-white disabled:opacity-50">
                {pending === 'internal' ? 'Registering…' : 'Register Basic commitments'}
              </button>
            ) : null}
            {protocol.allowedActions.prepareEmailReply ? (
              <>
                <button type="button" disabled={Boolean(pending) || !codeSaved} onClick={() => void run('email-app')} className="h-11 rounded-md bg-[#315f52] px-5 text-sm font-semibold text-white disabled:opacity-50">
                  {pending === 'email-app' ? 'Preparing…' : 'Open prepared reply in email app'}
                </button>
                <button type="button" disabled={Boolean(pending) || !codeSaved} onClick={() => void run('email-send')} className="h-11 rounded-md border border-[#315f52] bg-white px-5 text-sm font-semibold text-[#315f52] disabled:opacity-50">
                  {pending === 'email-send' ? 'Sending…' : 'Email me a reply-ready message'}
                </button>
              </>
            ) : null}
            {protocol.allowedActions.open ? (
              <button type="submit" disabled={Boolean(pending)} className="h-11 rounded-md bg-[#315f52] px-5 text-sm font-semibold text-white disabled:opacity-50">
                {pending === 'open' ? 'Recording…' : 'Open Part 2'}
              </button>
            ) : null}
            {protocol.allowedActions.accept ? (
              <button type="submit" disabled={Boolean(pending) || !protocol.fullContent} className="h-11 rounded-md bg-[#785a22] px-5 text-sm font-semibold text-white disabled:opacity-50">
                {pending === 'accept' ? 'Recording…' : 'Explicitly accept'}
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {protocol.allowedActions.dispute ? (
        <div className="mt-6 rounded-xl border border-[#d6b0ab] bg-[#fff7f6] p-4">
          <label className="text-sm font-semibold text-[#713d38]" htmlFor="opp-dispute">Dispute this record</label>
          <textarea id="opp-dispute" value={disputeReason} onChange={event => setDisputeReason(event.target.value)} className="mt-2 min-h-24 w-full rounded-md border border-[#c99d98] bg-white p-3 text-sm" />
          <button type="button" disabled={pending === 'dispute' || disputeReason.trim().length < 3} onClick={() => void run('dispute')} className="mt-2 h-10 rounded-md bg-[#8a4942] px-4 text-sm font-semibold text-white disabled:opacity-50">
            {pending === 'dispute' ? 'Recording…' : 'Record dispute'}
          </button>
        </div>
      ) : null}

      {protocol.lifecycleState !== 'setup' ? (
        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => void downloadOppPublicCertificate(protocol).catch(reason => setError(reason instanceof Error ? reason.message : String(reason)))}
            className="rounded-md border border-[#84958e] bg-white px-4 py-2 text-sm font-semibold text-[#315f52]"
          >
            Download public certificate
          </button>
          {protocol.allowedActions.saveEvidencePackage ? (
          <button
            type="button"
            onClick={() => void downloadOppEvidencePackage(protocol).catch(reason => setError(reason instanceof Error ? reason.message : String(reason)))}
            className="rounded-md border border-[#84958e] bg-white px-4 py-2 text-sm font-semibold text-[#315f52]"
          >
            Download evidence package
          </button>
          ) : null}
        </div>
      ) : null}

      <PooTransparencyTimeline events={protocol.transparencyEvents} />
    </section>
  )
}
